/* eslint-disable consistent-return */
/**
 * @file KidneyProgressView.jsx
 * @author Fernando Ruano
 * @description Kidney Progress View View
 */

// Dependencies
import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import moment from 'moment';

// Utils
import { getMonthShortName, sortByDate } from 'commons/helpers/utils';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';

// Assets
import { ReactComponent as ChartIcon } from '@nephro/assets/lineChartIcon.svg';

// Hooks
import { useTranslation } from 'react-i18next';
import usePatientIndicators from '../helpers/usePatientIndicators';
import { KIDNEY_PROGRESSION_INDICATOR } from '../helpers/constants';
import { getPatientStage } from '../helpers/utils';

const KidneyProgressView = () => {
  const { t: translate } = useTranslation();
  const [xPosition, setXPosition] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const kidneyProgress = usePatientIndicators(KIDNEY_PROGRESSION_INDICATOR);
  const [graphInfo, setGraphInfo] = useState(null);
  const [stageInfo, setStageInfo] = useState({
    status: '',
    value: '',
    previousStage: '',
    lastStage: ''
  });
  const isMobile = window.innerWidth < 768;

  const getStageDifference = (previous, last) => {
    const difference = last - previous;
    let status = 'stable';

    if (difference === 0) {
      status = 'stable';
    } else if (difference > 0) {
      status = 'increased';
    } else if (difference < 0) {
      status = 'decreased';
    }

    setStageInfo({
      status,
      value: Math.abs(difference).toFixed(3).slice(0, -1),
      previousStage: getPatientStage(previous),
      lastStage: getPatientStage(last)
    });
  };

  const getGraphData = () => {
    let labels = [];
    let tooltipDates = [];
    let data = [];

    const indicatorData = sortByDate(kidneyProgress, 'fecha', 'asc');
    indicatorData.forEach((item, idx) => {
      const date = new Date(item.fecha);
      const currYear = moment(date).year();
      const previousYear =
        idx > 0 ? moment(indicatorData[idx - 1].fecha).year() : false;
      const shortMonthName = getMonthShortName(date.getMonth());
      labels = [
        ...labels,
        `${translate(shortMonthName)} ${
          previousYear !== currYear || idx === 0 ? currYear : ''
        }`
      ];

      tooltipDates = [...tooltipDates, date.toLocaleDateString()];
      data = [...data, item.valor];
    });
    setGraphInfo({ labels, tooltipDates, data });
    getStageDifference(data[data.length - 2], data[data.length - 1]);
  };

  React.useEffect(() => {
    if (kidneyProgress.length) {
      getGraphData();
    }
  }, [kidneyProgress]);

  const data = {
    labels: graphInfo?.labels || [],
    datasets: [
      {
        label: 'anyyy',
        data: graphInfo?.data || [],
        fill: false,
        backgroundColor: '#003399',
        borderColor: '#003399',
        borderWidth: 3,
        pointBorderWidth: 8,
        pointHoverBorderWidth: 6,
        pointHoverBackgroundColor: '#fa8300',
        pointHoverBorderColor: '#fa8300'
      }
    ]
  };

  const showTooltip = () => {
    let leftPosition = xPosition + 1;
    if (selectedItem === 0) leftPosition = xPosition + 50;
    else if (selectedItem === graphInfo?.data?.length - 1)
      leftPosition = xPosition - 50;
    return (
      <>
        <div
          style={{
            backgroundColor: 'var(--color-grayletters)',
            padding: 10,
            position: 'relative',
            borderRadius: 10,
            width: 'fit-content',
            left: leftPosition,
            transform: 'translateX(-50%)'
          }}>
          <div className="flex flex-row items-end">
            <h3 className="text-stage-3a text-xl font-bold mb-0">
              {graphInfo?.data[selectedItem]}
            </h3>
            <h3 className="text-sm ml-2 mb-1 mt-2 text-secondary">
              mL/min/1,73m²
            </h3>
          </div>
          <h3 className="text-xs mb-0 text-secondary text-center">
            {graphInfo?.tooltipDates[selectedItem]}
          </h3>
        </div>
        <div
          style={{
            borderWidth: 1.8,
            borderColor: '#bac3d4',
            backgroundColor: '#bac3d4',
            paddingBottom: 24,
            marginBottom: -12,
            width: 1,
            position: 'relative',
            left:
              selectedItem === graphInfo?.data?.length - 1
                ? xPosition
                : xPosition + 1
          }}
        />
      </>
    );
  };

  const { lastStage, previousStage, status, value } = stageInfo;

  return (
    <AppCardLayout>
      <div className="h-full p-5 flex flex-col overflow-y-auto">
        <div className="flex flex-col items-left w-full">
          <div className="flex flex-row items-center">
            <ChartIcon width={40} />
            <h3 className="text-xl text-left mb-0 ml-2 text-baxterGray font-bold">
              {translate('indicators.kidneyProgress.title')}
            </h3>
          </div>
          <h3 className="text-lg text-left text-baxterGray mt-2">
            {translate('indicators.kidneyProgress.description')}
          </h3>
        </div>
        <div className="flex flex-col items-center">
          <div className="flex flex-col justify-center w-full md:max-w-3/5">
            {xPosition && showTooltip()}
            <Line
              data={data}
              options={{
                plugins: {
                  tooltip: {
                    enabled: false,
                    backgroundColor: '#e3e8ed',
                    external: (item) => {
                      const xPos = item.tooltip.caretX - 2.5;
                      if (xPos !== xPosition && item.tooltip.dataPoints) {
                        setXPosition(xPos);
                        setSelectedItem(item.tooltip.dataPoints[0].dataIndex);
                      }
                    }
                  },
                  legend: {
                    display: false,
                    title: { display: false },
                    labels: { color: 'red', boxWidth: 1000 }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Fechas'
                    }
                  },
                  y: {
                    /* grid: {
                      color: (context) => {
                        switch (context.index) {
                          case 3:
                            return '#00857d';
                          case 2:
                            return '#fcc180';
                          case 1:
                            return '#db0f00';
                          case 0:
                            return '#54585a';
                          default:
                            return null;
                        }
                      }
                    }, */
                    title: {
                      display: true,
                      text: 'TFG - mL/min/1,73m²'
                    },
                    min: 0,
                    ticks: {
                      stepSize:
                        isMobile &&
                        graphInfo?.data &&
                        Math.round(Math.max(...graphInfo?.data) / 4)
                    }
                  }
                }
              }}
            />
          </div>
          <div className="rounded-lg border-2 border-gray-300 p-2 mt-5 max-w-sm">
            <h3 className="text-lg text-left text-baxterGray">
              <span>
                {translate(`indicators.kidneyProgress.${status}`)}
                {(status === 'increased' || status === 'decreased') &&
                  `${value} mL/min/1,73m²`}
                {translate('indicators.kidneyProgress.footerText')}
              </span>
            </h3>
            <h3 className="text-lg text-left text-baxterGray flex flex-row">
              {previousStage === lastStage ? (
                translate('indicators.kidneyProgress.sameStage')
              ) : (
                <>
                  {translate('indicators.kidneyProgress.changeStage')}
                  <span className="text-stage-3a text-lg pl-1 pr-1">
                    {translate('indicators.stage')} {previousStage}
                  </span>
                  {translate('indicators.kidneyProgress.to')}
                  <span className="text-stage-3a text-lg pl-1 pr-1">
                    {translate('indicators.stage')} {lastStage}
                  </span>
                </>
              )}
            </h3>
          </div>
        </div>
      </div>
    </AppCardLayout>
  );
};

export default KidneyProgressView;
